// Path: src/lib/utils/videoRecording.ts
// Description: 视频录制能力探测与元数据生成工具函数

/**
 * 视频录制 MIME 类型优先级列表
 * 按照阿里云视频活体检测兼容性排序
 */
const MIME_TYPE_PRIORITY = [
  'video/mp4; codecs="avc1.424028, mp4a.40.2"',
  'video/mp4; codecs="avc1.42E01E, mp4a.40.2"',
  'video/mp4',
  'video/webm; codecs="vp9,opus"',
  'video/webm; codecs="vp8,opus"',
  'video/webm',
];

/**
 * 录制能力探测结果
 */
export interface RecordingCapability {
  requestedMimeType: string;
  isSupported: boolean;
  supportedMimeTypes: string[];
}

/**
 * 录制元数据
 */
export interface RecordingMetadata {
  record_requested_mime_type: string;
  record_actual_mime_type: string;
  record_file_ext: string;
  record_duration_ms: number;
  record_video_width: number;
  record_video_height: number;
  record_video_fps_expected: number;
  user_agent: string;
  platform: string;
  is_mobile: boolean;
  camera_facing_mode: string;
}

/**
 * 探测浏览器最佳录制 MIME 类型
 * @returns RecordingCapability 探测结果
 */
export function getBestRecordingMimeType(): RecordingCapability {
  const supportedMimeTypes: string[] = [];
  let bestMimeType = '';
  
  for (const mimeType of MIME_TYPE_PRIORITY) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      supportedMimeTypes.push(mimeType);
      if (!bestMimeType) {
        bestMimeType = mimeType;
      }
    }
  }
  
  if (!bestMimeType) {
    bestMimeType = '';
  }
  
  return {
    requestedMimeType: bestMimeType,
    isSupported: bestMimeType !== '',
    supportedMimeTypes,
  };
}

/**
 * 根据 mimeType 获取文件扩展名
 * @param mimeType 实际录制的 mimeType
 * @returns 文件扩展名 (不含点号)
 */
export function getFileExtensionFromMimeType(mimeType: string): string {
  if (mimeType.includes('mp4')) {
    return 'mp4';
  }
  if (mimeType.includes('webm')) {
    return 'webm';
  }
  if (mimeType.includes('ogg')) {
    return 'ogg';
  }
  return 'webm';
}

/**
 * 生成录制元数据
 */
export function getRecordingDebugMeta(params: {
  requestedMimeType: string;
  actualMimeType: string;
  durationMs: number;
  videoWidth: number;
  videoHeight: number;
  facingMode: string;
}): RecordingMetadata {
  const {
    requestedMimeType,
    actualMimeType,
    durationMs,
    videoWidth,
    videoHeight,
    facingMode,
  } = params;
  
  const fileExt = getFileExtensionFromMimeType(actualMimeType);
  
  return {
    record_requested_mime_type: requestedMimeType,
    record_actual_mime_type: actualMimeType,
    record_file_ext: fileExt,
    record_duration_ms: Math.round(durationMs),
    record_video_width: videoWidth,
    record_video_height: videoHeight,
    record_video_fps_expected: 30,
    user_agent: navigator.userAgent,
    platform: navigator.platform,
    is_mobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
    camera_facing_mode: facingMode,
  };
}

/**
 * 构建人脸录入上传载荷
 * @param videoBlob 视频文件
 * @param imageBlob 图片文件
 * @param metadata 录制元数据
 * @returns FormData
 */
export function buildFaceRecordUploadPayload(
  videoBlob: Blob,
  imageBlob: Blob,
  metadata: RecordingMetadata
): FormData {
  const formData = new FormData();
  
  const videoExt = metadata.record_file_ext;
  const videoFileName = `face_record.${videoExt}`;
  const imageFileName = 'face_capture.jpg';
  
  formData.append('video', videoBlob, videoFileName);
  formData.append('image', imageBlob, imageFileName);
  formData.append('record_requested_mime_type', metadata.record_requested_mime_type);
  formData.append('record_actual_mime_type', metadata.record_actual_mime_type);
  formData.append('record_file_ext', metadata.record_file_ext);
  formData.append('record_duration_ms', metadata.record_duration_ms.toString());
  formData.append('record_video_width', metadata.record_video_width.toString());
  formData.append('record_video_height', metadata.record_video_height.toString());
  formData.append('record_video_fps_expected', metadata.record_video_fps_expected.toString());
  formData.append('user_agent', metadata.user_agent);
  formData.append('platform', metadata.platform);
  formData.append('is_mobile', metadata.is_mobile.toString());
  formData.append('camera_facing_mode', metadata.camera_facing_mode);
  
  return formData;
}

/**
 * 录制时长校验
 * @param durationMs 录制时长(毫秒)
 * @param minDurationMs 最小时长(毫秒)，默认3000ms
 * @returns 是否通过校验
 */
export function validateRecordingDuration(durationMs: number, minDurationMs: number = 3000): {
  valid: boolean;
  message: string;
} {
  if (durationMs < minDurationMs) {
    return {
      valid: false,
      message: `录制时长不足，请至少录制 ${Math.ceil(minDurationMs / 1000)} 秒`,
    };
  }
  return {
    valid: true,
    message: '录制时长符合要求',
  };
}

/**
 * 输出调试信息 (仅开发环境)
 */
export function logRecordingDebugInfo(info: {
  supportedMimeTypes: string[];
  requestedMimeType: string;
  actualMimeType: string;
  blobType: string;
  fileName: string;
  fileSize: number;
  durationMs: number;
}): void {
  if (import.meta.env.DEV) {
    console.group('📹 视频录制调试信息');
    console.log('浏览器支持的 MIME 类型:', info.supportedMimeTypes);
    console.log('请求的 MIME 类型:', info.requestedMimeType);
    console.log('实际录制的 MIME 类型:', info.actualMimeType);
    console.log('Blob.type:', info.blobType);
    console.log('文件名:', info.fileName);
    console.log('文件大小:', (info.fileSize / 1024).toFixed(2), 'KB');
    console.log('录制时长:', (info.durationMs / 1000).toFixed(2), '秒');
    console.groupEnd();
  }
}

/**
 * 录制错误类型
 */
export const RECORDING_ERRORS = {
  MEDIA_RECORDER_NOT_SUPPORTED: '您的浏览器不支持视频录制功能，请使用Chrome或Edge浏览器',
  CAMERA_ACCESS_FAILED: '无法访问摄像头，请检查摄像头是否被其他程序占用',
  CAMERA_PERMISSION_DENIED: '摄像头权限被拒绝，请在浏览器设置中允许访问摄像头',
  RECORDING_TOO_SHORT: '录制时间过短，请保持至少3秒',
  RECORDING_EMPTY: '录制失败，视频内容为空',
  RECORDING_START_FAILED: '录制启动失败，请重试',
  UPLOAD_FAILED: '上传失败，请检查网络连接',
};

/**
 * 解析录制错误
 * @param error 错误对象
 * @returns 用户友好的错误消息
 */
export function parseRecordingError(error: any): string {
  const errorName = error?.name || '';
  const errorMessage = error?.message || '';
  
  if (errorName === 'NotAllowedError' || errorMessage.includes('Permission')) {
    return RECORDING_ERRORS.CAMERA_PERMISSION_DENIED;
  }
  if (errorName === 'NotFoundError' || errorMessage.includes('not found')) {
    return RECORDING_ERRORS.CAMERA_ACCESS_FAILED;
  }
  if (errorName === 'NotReadableError' || errorMessage.includes('not readable')) {
    return RECORDING_ERRORS.CAMERA_ACCESS_FAILED;
  }
  if (errorName === 'NotSupportedError') {
    return RECORDING_ERRORS.MEDIA_RECORDER_NOT_SUPPORTED;
  }
  
  return errorMessage || RECORDING_ERRORS.RECORDING_START_FAILED;
}
